import { forEach } from 'lodash';
import * as Constants from '../constants/Constants';
import * as ActionTypes from '../constants/ActionTypes';
import areValuesEqual from './utils/areValuesEqual';
import moment from 'moment';
import '../utils/moment-extensions';
import { Store } from '../utils/flux';
import * as TimesGridStateStoreEvents from '../constants/TimesGridStateStoreEvents';

/**
 * @class TimesGridStateStore
 * @extends Store
 */
export default class TimesGridStateStore extends Store {
    constructor(options) {
        super(options);

        this.doctorLocationIds = options.doctorLocationIds;
        this.newPatientTimesStore = options.newPatientTimesStore;
        this.existingPatientTimesStore = options.existingPatientTimesStore;
        this.state = options.state;
        this.state.moreBtnId = 1;
        this.timesStore = options.state.isExistingPatient ?
            this.existingPatientTimesStore :
            this.newPatientTimesStore;
    }

    /**
     * @param {object} newState
     */
    changeState(newState) {
        const events = [];

        forEach(newState, (value, property) => {
            const currentValue = this.state[property];

            if (areValuesEqual(currentValue, value)) {
                return;
            }

            this.state[property] = value;

            switch (property) {
                case 'availabilityTimeFilter':
                    events.push(TimesGridStateStoreEvents.CHANGE_AVAILABILITY_TIME_FILTER);
                    break;

                case 'date':
                    events.push(TimesGridStateStoreEvents.CHANGE_DATE);
                    break;

                case 'gridType':
                    events.push(TimesGridStateStoreEvents.CHANGE_GRID_TYPE);
                    break;

                case 'insuranceCarrier':
                case 'insurancePlan':
                    events.push(TimesGridStateStoreEvents.CHANGE_INSURANCE);
                    break;

                case 'procedureId':
                    events.push(TimesGridStateStoreEvents.CHANGE_PROCEDURE);
                    break;

                case 'isExistingPatient':
                    this.timesStore = this.state.isExistingPatient ?
                        this.existingPatientTimesStore :
                        this.newPatientTimesStore;
                    events.push(TimesGridStateStoreEvents.CHANGE_IS_EXISTING_PATIENT);
                    break;

                case 'doctorLocationIds':
                    events.push(TimesGridStateStoreEvents.CHANGE_DOCTOR_LOCATION_IDS);
                    break;

                default:
                    break;
            }
        });

        if (events.length) {
            this.trigger(events.join(' '));
        }
    }

    nextDays() {
        const newDate = moment(this.state.date).add(this.state.numDaysVisible, 'days');

        this.changeState({
            date: newDate,
            moreBtnId: this.state.moreBtnId + 1,
        });
    }

    prevDays() {
        let newDate = moment(this.state.date).add(-this.state.numDaysVisible, 'days');

        if (newDate.isBeforeToday()) {
            newDate = moment();
        }

        this.changeState({
            date: newDate,
            moreBtnId: this.state.moreBtnId + 1,
        });
    }

    /**
     * @param {moment} date
     */
    jumpToDate(date) {
        let newDate = moment(date);

        if (newDate.isBeforeToday()) {
            newDate = moment();
        }

        this.changeState({
            date: newDate,
        });
    }

    /**
     * @returns {object}
     */
    getState() {
        return this.state;
    }

    /**
     * @param {object} payload
     * @param {string} payload.actionType
     * @param {boolean} payload.isExisting
     * @param {int} [payload.availabilityTimeFilter]
     * @param {int} [payload.numDaysVisible]
     * @param {string} [payload.procedureId]
     * @param {moment} [payload.date]
     */
    onDispatch(payload) {
        this.waitFor(this.timesStore.getDispatchToken());

        switch (payload.actionType) {
            case ActionTypes.CHANGE_AVAILABILITY_TIME_FILTER:
                this.changeState({
                    availabilityTimeFilter: payload.availabilityTimeFilter,
                });
                break;

            case ActionTypes.CHANGE_GRID_TYPE:
                this.changeState({
                    gridType: Constants.TYPE[payload.isMobile ? 'MOBILE' : 'DESKTOP'],
                });
                break;

            case ActionTypes.CHANGE_NUM_DAYS_VISIBLE:
                this.changeState({
                    numDaysVisible: payload.numDaysVisible,
                });
                break;

            case ActionTypes.CHANGE_INSURANCE:
                this.changeState({
                    insuranceCarrier: payload.insuranceCarrier,
                    insurancePlan: payload.insurancePlan,
                });
                break;

            case ActionTypes.CHANGE_PROCEDURE:
                this.changeState({
                    procedureId: payload.procedureId,
                });
                break;

            case ActionTypes.CLICK_NEXT_DAYS:
                this.nextDays();
                break;

            case ActionTypes.CLICK_PREV_DAYS:
                this.prevDays();
                break;

            case ActionTypes.JUMP_TO_DATE:
                this.jumpToDate(payload.date);
                break;

            case ActionTypes.CHANGE_IS_EXISTING_PATIENT:
                this.changeState({
                    isExistingPatient: payload.isExisting,
                });
                break;

            case ActionTypes.CHANGE_DOCTOR_LOCATION_IDS:
                this.doctorLocationIds = payload.doctorLocationIds;
                this.changeState({
                    doctorLocationIds: payload.doctorLocationIds,
                });
                break;

            case ActionTypes.ALL_TIMES_RECEIVED:
                this.changeState({
                    initialized: true,
                });
                break;

            case ActionTypes.SERVER_ERROR:
                break;
        }
    }
}
